import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2 } from 'lucide-react';
import { CATEGORIES } from '../../lib/categories';

export function PortfolioTab({
  portfolio, portfolioCaption, portfolioCategory,
  setPortfolioCaption, setPortfolioCategory,
  handlePortfolioUpload, uploadingImage, handlePortfolioDelete
}) {
  return (
    <div className="space-y-6">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Your Portfolio</h3>
          <span className="text-sm text-stone-500">{portfolio.length}/10 images</span>
        </div>
        
        {portfolio.length < 10 && (
          <div className="space-y-3 mb-6 p-4 bg-[#F9F6F0] rounded-lg">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="portfolio-caption" className="text-xs">Caption</Label>
                <Input
                  id="portfolio-caption"
                  value={portfolioCaption}
                  onChange={(e) => setPortfolioCaption(e.target.value)}
                  placeholder="e.g. Knotless braids - waist length"
                  className="input-zuriya"
                  data-testid="portfolio-caption-input"
                />
              </div>
              <div>
                <Label htmlFor="portfolio-category" className="text-xs">Category (optional)</Label>
                <Select value={portfolioCategory} onValueChange={setPortfolioCategory}>
                  <SelectTrigger className="input-zuriya" data-testid="portfolio-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="portfolio-upload" className="text-xs">Upload Image (max 5MB)</Label>
              <div className="flex gap-2 mt-1">
                <input
                  id="portfolio-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePortfolioUpload}
                  disabled={uploadingImage}
                  className="flex-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF37] file:text-white file:text-sm file:font-medium file:cursor-pointer hover:file:opacity-90"
                  data-testid="portfolio-upload-input"
                />
              </div>
              {uploadingImage && <p className="text-xs text-[#D4AF37] mt-1">Uploading...</p>}
            </div>
          </div>
        )}

        {portfolio.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-stone-500 mb-1">No portfolio images yet</p>
            <p className="text-xs text-stone-400">Upload photos of your best work to attract clients</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="portfolio-grid">
            {portfolio.map((img) => (
              <div key={img.image_id} className="group relative rounded-lg overflow-hidden bg-stone-100 aspect-square" data-testid={`portfolio-image-${img.image_id}`}>
                <img src={img.url} alt={img.caption || 'Portfolio'} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                  <div className="w-full p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                    {img.caption && <p className="text-white text-xs font-medium truncate">{img.caption}</p>}
                    <div className="flex items-center justify-between mt-1">
                      {img.category && <span className="text-xs text-white/80 capitalize">{img.category}</span>}
                      <button onClick={() => handlePortfolioDelete(img.image_id)} className="p-1 bg-red-500/80 hover:bg-red-600 rounded text-white ml-auto" data-testid={`delete-portfolio-${img.image_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
